// hooks/useCatalogOperations.js
import { useMutation, useApolloClient } from '@apollo/client';
import { CREATE_ITEM, CREATE_CATEGORY, UPDATE_ITEM, DELETE_ITEM, UPDATE_CATEGORY, DELETE_CATEGORY, REORDER_CATALOG } from '../graphql/mutations';
import { GET_CATALOG_ITEMS_AND_CATEGORIES } from '../graphql/queries';
import { usePlanLimits } from './usePlanLimits';

const DEFAULT_ITEM = {
    name: '',
    description: '',
    priceVariants: [],
    image: null,
    category: null,
    isVisible: true,
    order: undefined,
    clickCount: undefined,
    // Add digital product defaults
    productType: 'PHYSICAL',
    digitalAssets: [],
    digitalSettings: null,
    requiresShipping: true,
    pendingDigitalAssets: []
};

const DEFAULT_CATEGORY = {
    name: '',
    isVisible: true,
    items: [],
};

export const useCatalogOperations = (catalogId) => {
    const [createItem] = useMutation(CREATE_ITEM);
    const [createCategory] = useMutation(CREATE_CATEGORY);
    const [updateItem] = useMutation(UPDATE_ITEM);
    const [deleteItem] = useMutation(DELETE_ITEM);
    const [updateCategory] = useMutation(UPDATE_CATEGORY);
    const [deleteCategory] = useMutation(DELETE_CATEGORY);
    const [reorderCatalog] = useMutation(REORDER_CATALOG);
    const client = useApolloClient();
    const { checkCanAdd, checkDigitalFileSize, checkDigitalFileCount, checkTotalDigitalSize, limits } = usePlanLimits(catalogId);

    const generateItemOptimisticResponse = (currentData = {}, newData = {}) => {
        const data = {
            ...DEFAULT_ITEM,
            ...currentData,
            ...newData,
        };

        return {
            __typename: 'Mutation',
            createItem: {
                __typename: 'Item',
                id: 'temp-id-' + Date.now(),
                ...data,
            },
        };
    };

    const generateCategoryOptimisticResponse = (currentData = {}, newData = {}) => {
        const data = {
            ...DEFAULT_CATEGORY,
            ...currentData,
            ...newData,
        };

        return {
            __typename: 'Mutation',
            createCategory: {
                __typename: 'Category',
                id: 'temp-id-' + Date.now(),
                ...data,
            },
        };
    };

    const optimizedCreateItem = async (variables) => {
        // Check plan limits before creating
        const limitCheck = checkCanAdd('product');
        if (!limitCheck.canAdd) {
            throw new Error(limitCheck.message);
        }

        // If creating a digital product, validate digital assets
        if (variables.productType === 'DIGITAL' && variables.pendingDigitalAssets?.length > 0) {
            // Check file count
            const countCheck = checkDigitalFileCount(0, variables.pendingDigitalAssets.length);
            if (!countCheck.canAdd) {
                throw new Error(countCheck.message);
            }

            // Check individual file sizes and total size
            let totalSize = 0;
            for (const asset of variables.pendingDigitalAssets) {
                // Check individual file size
                const sizeCheck = checkDigitalFileSize(asset.fileSize);
                if (!sizeCheck.canAdd) {
                    throw new Error(`${asset.originalName}: ${sizeCheck.message}`);
                }
                totalSize += asset.fileSize;
            }

            // Check total size
            const totalCheck = checkTotalDigitalSize(0, totalSize);
            if (!totalCheck.canAdd) {
                throw new Error(totalCheck.message);
            }
        }

        // Clean up and prepare variables for digital products
        const cleanedVariables = {
            ...variables,
            priceVariants: variables.priceVariants?.map(variant => ({
                variant: variant.variant,
                price: variant.price,
                compareAtPrice: variant.compareAtPrice,
                sku: variant.sku,
                stock: variant.stock,
                lowStockThreshold: variant.lowStockThreshold,
            })),
            // Handle digital product fields
            productType: variables.productType || 'PHYSICAL',
            requiresShipping: variables.requiresShipping !== undefined ? variables.requiresShipping :
                (variables.productType === 'DIGITAL' ? false : true),
            // Include digital settings if provided
            ...(variables.digitalSettings && {
                digitalSettings: {
                    downloadLimit: variables.digitalSettings.downloadLimit || -1,
                    linkExpiryHours: variables.digitalSettings.linkExpiryHours || -1,
                    allowPreview: variables.digitalSettings.allowPreview || false,
                    previewDuration: variables.digitalSettings.previewDuration
                }
            }),
            // Include pending digital assets if provided
            ...(variables.pendingDigitalAssets && {
                pendingDigitalAssets: variables.pendingDigitalAssets.map(asset => ({
                    filename: asset.filename,
                    originalName: asset.originalName,
                    fileSize: asset.fileSize,
                    fileType: asset.fileType,
                    isTemp: asset.isTemp,
                    uploadMethod: asset.uploadMethod,
                    assetType: asset.assetType,
                    ...(asset.uploadMethod === 'base64' && { base64Data: asset.base64Data }),
                    ...(asset.storageUrl && { storageUrl: asset.storageUrl })
                }))
            })
        };

        return createItem({
            variables: cleanedVariables,
            optimisticResponse: {
                __typename: 'Mutation',
                createItem: {
                    __typename: 'Item',
                    id: `temp-id-${Date.now()}`,
                    ...cleanedVariables,
                    isVisible: true,
                    digitalAssets: [], // Will be populated after server processing
                }
            },
            update: (cache, { data }) => {
                const newItem = data?.createItem;
                if (!newItem) return;

                try {
                    const existingData = cache.readQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId }
                    });

                    if (!existingData) return;

                    const updatedData = {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            categories: existingData.catalogItemsAndCategories.categories.map(category => {
                                if (category.id === newItem.category) {
                                    return {
                                        ...category,
                                        items: [...category.items, newItem]
                                    };
                                }
                                return category;
                            }),
                            uncategorizedItems: !newItem.category
                                ? [...existingData.catalogItemsAndCategories.uncategorizedItems, newItem]
                                : existingData.catalogItemsAndCategories.uncategorizedItems
                        }
                    };

                    cache.writeQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                        data: updatedData
                    });
                } catch (error) {
                    console.error('Cache update error:', error);
                    throw new Error('Failed to update cache after item creation');
                }
            }
        });
    };

    const optimizedCreateCategory = async (variables) => {
        // Check plan limits before creating
        const limitCheck = checkCanAdd('category');
        if (!limitCheck.canAdd) {
            throw new Error(limitCheck.message);
        }

        return createCategory({
            variables,
            optimisticResponse: generateCategoryOptimisticResponse({}, variables),
            update: (cache, { data }) => {
                const newCategory = data?.createCategory;
                if (!newCategory) return;

                const existingData = cache.readQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId },
                });

                if (!existingData) return;

                const updatedData = {
                    catalogItemsAndCategories: {
                        ...existingData.catalogItemsAndCategories,
                        categories: [...existingData.catalogItemsAndCategories.categories, newCategory],
                    },
                };

                cache.writeQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId },
                    data: updatedData,
                });
            },
        });
    };

    const optimizedDeleteItem = async (item) => {
        if (!item) {
            throw new Error('Item is required');
        }

        return deleteItem({
            variables: { id: item.id },
            optimisticResponse: {
                __typename: 'Mutation',
                deleteItem: 'Item removed'
            },
            update: (cache) => {
                const existingData = cache.readQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId },
                });

                if (!existingData) return;

                const itemId = item.id;
                const updatedData = {
                    catalogItemsAndCategories: {
                        ...existingData.catalogItemsAndCategories,
                        categories: existingData.catalogItemsAndCategories.categories.map(category => ({
                            ...category,
                            items: category.items.filter(item => item.id !== itemId),
                        })),
                        uncategorizedItems: existingData.catalogItemsAndCategories.uncategorizedItems
                            .filter(item => item.id !== itemId),
                    },
                };

                cache.writeQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId },
                    data: updatedData,
                });
            },
        });
    };

    const optimizedUpdateItem = async (variables) => {
        const { id, image, ...updates } = variables;

        // Get existing item data from cache
        const existingData = client.readQuery({
            query: GET_CATALOG_ITEMS_AND_CATEGORIES,
            variables: { catalogId }
        });

        // Find current item data
        const currentItem = existingData?.catalogItemsAndCategories?.categories
            .reduce((found, category) => {
                if (found) return found;
                return category.items.find(item => item.id === id);
            }, null) ||
            existingData?.catalogItemsAndCategories?.uncategorizedItems
                .find(item => item.id === id);

        if (!currentItem) {
            console.error('Item not found in cache');
            return;
        }

        // Validate digital assets if being updated
        if (updates.pendingDigitalAssets?.length > 0) {
            const currentAssetCount = currentItem.digitalAssets?.length || 0;
            const newAssetCount = updates.pendingDigitalAssets.length;

            // Check file count
            const countCheck = checkDigitalFileCount(currentAssetCount, newAssetCount);
            if (!countCheck.canAdd) {
                throw new Error(countCheck.message);
            }

            // Check sizes
            const currentTotalSize = currentItem.digitalAssets?.reduce((sum, a) => sum + (a.fileSize || 0), 0) || 0;
            let newTotalSize = 0;

            for (const asset of updates.pendingDigitalAssets) {
                const sizeCheck = checkDigitalFileSize(asset.fileSize);
                if (!sizeCheck.canAdd) {
                    throw new Error(`${asset.originalName}: ${sizeCheck.message}`);
                }
                newTotalSize += asset.fileSize;
            }

            const totalCheck = checkTotalDigitalSize(currentTotalSize, newTotalSize);
            if (!totalCheck.canAdd) {
                throw new Error(totalCheck.message);
            }
        }

        // Process digital product updates
        const processedUpdates = { ...updates };

        // Handle digital settings updates
        if (updates.digitalSettings) {
            processedUpdates.digitalSettings = {
                downloadLimit: updates.digitalSettings.downloadLimit ?? -1,
                linkExpiryHours: updates.digitalSettings.linkExpiryHours ?? -1,
                allowPreview: updates.digitalSettings.allowPreview ?? false,
                previewDuration: updates.digitalSettings.previewDuration
            };
        }

        // Handle product type changes
        if (updates.productType) {
            processedUpdates.requiresShipping = updates.productType === 'DIGITAL' ? false :
                (updates.requiresShipping !== undefined ? updates.requiresShipping : true);
        }

        // Only include fields that have actually changed
        const changedFields = {};
        Object.keys(processedUpdates).forEach(key => {
            if (JSON.stringify(processedUpdates[key]) !== JSON.stringify(currentItem[key])) {
                changedFields[key] = processedUpdates[key];
            }
        });

        return updateItem({
            variables: { id, image, ...changedFields },
            optimisticResponse: {
                __typename: 'Mutation',
                updateItem: {
                    ...currentItem,
                    ...changedFields,
                    __typename: 'Item',
                    id,
                }
            },
            update: (cache, { data }) => {
                const updatedItem = data?.updateItem;
                if (!updatedItem) return;

                const existingData = cache.readQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId }
                });

                if (!existingData) return;

                // Update items in all categories
                const updatedCategories = existingData.catalogItemsAndCategories.categories.map(category => ({
                    ...category,
                    items: category.items.map(item =>
                        item.id === id ? updatedItem : item
                    )
                }));

                // Update uncategorized items
                const updatedUncategorizedItems = existingData.catalogItemsAndCategories.uncategorizedItems
                    .map(item => item.id === id ? updatedItem : item);

                cache.writeQuery({
                    query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                    variables: { catalogId },
                    data: {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            categories: updatedCategories,
                            uncategorizedItems: updatedUncategorizedItems
                        }
                    }
                });
            }
        });
    };


    const optimizedUpdateCategory = async (variables) => {
        const { id, ...updates } = variables;

        return updateCategory({
            variables,
            optimisticResponse: {
                __typename: 'Mutation',
                updateCategory: {
                    __typename: 'Category',
                    id,
                    ...updates,
                },
            },
            update: (cache, { data }) => {
                const updatedCategory = data?.updateCategory;
                if (!updatedCategory) return;

                // Update category fields in cache
                cache.modify({
                    id: cache.identify({ __typename: 'Category', id }),
                    fields: Object.keys(updates).reduce((acc, key) => ({
                        ...acc,
                        [key]: () => updates[key],
                    }), {}),
                });

                // Update the category in the GET_CATALOG_ITEMS_AND_CATEGORIES query
                try {
                    const existingData = cache.readQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                    });

                    if (!existingData) return;

                    const updatedData = {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            categories: existingData.catalogItemsAndCategories.categories.map(category =>
                                category.id === id ? { ...category, ...updates } : category
                            ),
                        },
                    };

                    cache.writeQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                        data: updatedData,
                    });
                } catch (error) {
                    console.error('Cache update error:', error);
                }
            },
        });
    };

    const optimizedDeleteCategory = async (categoryId) => {
        return deleteCategory({
            variables: { id: categoryId },
            optimisticResponse: {
                __typename: 'Mutation',
                deleteCategory: 'Category removed'
            },
            update: (cache, { data }) => {
                try {
                    // Read the existing data
                    const existingData = cache.readQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId }
                    });

                    if (!existingData) return;

                    // Find the category being deleted to get its items
                    const categoryToDelete = existingData.catalogItemsAndCategories.categories
                        .find(cat => cat.id === categoryId);
                    const itemsToDelete = categoryToDelete?.items || [];

                    // Remove all items in the category from cache
                    itemsToDelete.forEach(item => {
                        cache.evict({
                            id: cache.identify({ __typename: 'Item', id: item.id })
                        });
                    });

                    // Update the main catalog data
                    const updatedData = {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            categories: existingData.catalogItemsAndCategories.categories
                                .filter(category => category.id !== categoryId)
                        }
                    };

                    // Write the updated data back to cache
                    cache.writeQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                        data: updatedData
                    });

                    // Remove the category from the cache
                    cache.evict({ id: cache.identify({ __typename: 'Category', id: categoryId }) });
                    cache.gc();
                } catch (error) {
                    console.error('Cache update error:', error);
                    throw error;
                }
            }
        });
    };

    const optimizedToggleItemVisibility = async (itemId, currentVisibility) => {
        if (typeof itemId !== 'string' || typeof currentVisibility !== 'boolean') {
            throw new Error('Invalid parameters: itemId must be a string and currentVisibility must be a boolean');
        }

        return updateItem({
            variables: {
                id: itemId,
                isVisible: !currentVisibility
            },
            optimisticResponse: {
                __typename: 'Mutation',
                updateItem: {
                    __typename: 'Item',
                    id: itemId,
                    isVisible: !currentVisibility
                }
            },
            update: (cache, { data }) => {
                const updatedItem = data?.updateItem;
                if (!updatedItem) return;

                // Update the item's visibility in the cache
                cache.modify({
                    id: cache.identify({ __typename: 'Item', id: itemId }),
                    fields: {
                        isVisible: () => !currentVisibility
                    }
                });

                // Update the item in the main catalog query
                try {
                    const existingData = cache.readQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId }
                    });

                    if (!existingData) return;

                    const updateItemInList = (items) =>
                        items.map(item =>
                            item.id === itemId
                                ? { ...item, isVisible: !currentVisibility }
                                : item
                        );

                    const updatedData = {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            // Update items in categories
                            categories: existingData.catalogItemsAndCategories.categories.map(category => ({
                                ...category,
                                items: category.items ? updateItemInList(category.items) : []
                            })),
                            // Update uncategorized items
                            uncategorizedItems: updateItemInList(
                                existingData.catalogItemsAndCategories.uncategorizedItems
                            )
                        }
                    };

                    cache.writeQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                        data: updatedData
                    });
                } catch (error) {
                    console.error('Cache update error:', error);
                    // Even if the cache update fails, the server update might have succeeded
                    // Consider refetching the data in this case
                    return;
                }
            }
        });
    };

    const optimizedToggleCategoryVisibility = async (categoryId, currentVisibility) => {
        if (typeof categoryId !== 'string' || typeof currentVisibility !== 'boolean') {
            throw new Error('Invalid parameters: categoryId must be a string and currentVisibility must be a boolean');
        }

        return updateCategory({
            variables: {
                id: categoryId,
                isVisible: !currentVisibility
            },
            optimisticResponse: {
                __typename: 'Mutation',
                updateCategory: {
                    __typename: 'Category',
                    id: categoryId,
                    isVisible: !currentVisibility
                }
            },
            update: (cache, { data }) => {
                const updatedCategory = data?.updateCategory;
                if (!updatedCategory) return;

                // Update the individual category's visibility in the cache
                cache.modify({
                    id: cache.identify({ __typename: 'Category', id: categoryId }),
                    fields: {
                        isVisible: () => !currentVisibility
                    }
                });

                // Update the category in the main catalog query
                try {
                    const existingData = cache.readQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId }
                    });

                    if (!existingData) return;

                    const updatedData = {
                        catalogItemsAndCategories: {
                            ...existingData.catalogItemsAndCategories,
                            categories: existingData.catalogItemsAndCategories.categories.map(category =>
                                category.id === categoryId
                                    ? { ...category, isVisible: !currentVisibility }
                                    : category
                            )
                        }
                    };

                    cache.writeQuery({
                        query: GET_CATALOG_ITEMS_AND_CATEGORIES,
                        variables: { catalogId },
                        data: updatedData
                    });
                } catch (error) {
                    console.error('Cache update error:', error);
                    // Consider refetching if cache update fails
                    throw new Error('Failed to update category visibility in cache');
                }
            }
        });
    };

    return {
        optimizedCreateItem,
        optimizedCreateCategory,
        optimizedDeleteItem,
        optimizedUpdateItem,
        optimizedUpdateCategory,
        optimizedDeleteCategory,
        optimizedToggleItemVisibility,
        optimizedToggleCategoryVisibility,
        // Expose plan limit checks

        checkCanAdd,
        checkDigitalFileSize,
        checkDigitalFileCount,
        checkTotalDigitalSize,
        limits
    };
};